import { z } from 'zod';
import { isRealDate, paginationQuerySchema, uuidSchema } from './common.js';
import { MAX_MINOR, minorSchema } from './fees.js';
const text = (max) => z
    .string()
    .trim()
    .max(max)
    .refine((v) => !v.includes('\u0000'), 'Invalid character');
const optionalText = (max) => text(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const isoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
    .refine(isRealDate, 'Invalid calendar date');
const atLeastOne = (v) => Object.keys(v).length > 0;
const halfDays = z
    .number()
    .min(0)
    .max(366)
    .refine((v) => Math.abs(v * 2 - Math.round(v * 2)) < 1e-9, 'Whole or half days only');
// -------------------------------------------------------- staff attendance
/** `on_leave` is set only by approving a leave request, never by hand. */
export const STAFF_ATTENDANCE_MARKS = ['present', 'absent', 'late', 'half_day'];
export const markStaffAttendanceSchema = z
    .object({
    records: z
        .array(z.object({
        staffId: uuidSchema,
        status: z.enum(STAFF_ATTENDANCE_MARKS),
        remarks: optionalText(300),
    }))
        .min(1)
        .max(300),
})
    .refine((v) => new Set(v.records.map((r) => r.staffId)).size === v.records.length, {
    message: 'A staff member can appear only once',
    path: ['records'],
});
export const staffAttendanceRangeSchema = z
    .object({ from: isoDate, to: isoDate })
    .refine((v) => v.to >= v.from && Date.parse(v.to) - Date.parse(v.from) <= 366 * 86_400_000, {
    message: 'Range must be ordered and at most 366 days',
    path: ['to'],
});
// ------------------------------------------------------------------- leave
export const createLeaveTypeSchema = z.object({
    name: text(60).min(1),
    /** null = unlimited (e.g. unpaid leave). */
    annualDays: halfDays.nullable().default(null),
    isPaid: z.boolean().default(true),
});
export const updateLeaveTypeSchema = z
    .object({
    name: text(60).min(1),
    annualDays: halfDays.nullable(),
    isPaid: z.boolean(),
    isActive: z.boolean(),
})
    .partial()
    .refine(atLeastOne, 'At least one field is required');
export const createStaffLeaveSchema = z
    .object({ leaveTypeId: uuidSchema, fromDate: isoDate, toDate: isoDate, reason: text(500).min(1) })
    .refine((v) => v.toDate >= v.fromDate, {
    message: 'End date cannot be before start date',
    path: ['toDate'],
})
    .refine((v) => v.fromDate.slice(0, 4) === v.toDate.slice(0, 4), {
    message: 'A request must stay within one calendar year',
    path: ['toDate'],
});
export const reviewStaffLeaveSchema = z.object({
    decision: z.enum(['approved', 'rejected']),
    note: optionalText(500),
});
export const listStaffLeaveQuerySchema = paginationQuerySchema.extend({
    status: z.enum(['pending', 'approved', 'rejected', 'cancelled']).optional(),
    staffId: uuidSchema.optional(),
    year: z.coerce.number().int().min(2000).max(2200).optional(),
});
// ------------------------------------------------------------------ salary
export const SALARY_KINDS = ['earning', 'deduction'];
export const createSalaryComponentSchema = z.object({
    name: text(60).min(1),
    kind: z.enum(SALARY_KINDS),
});
export const updateSalaryComponentSchema = z
    .object({ name: text(60).min(1), isActive: z.boolean() })
    .partial()
    .refine(atLeastOne, 'At least one field is required');
export const setStaffSalarySchema = z
    .object({
    effectiveFrom: isoDate,
    basicMinor: minorSchema,
    components: z
        .array(z.object({ componentId: uuidSchema, amountMinor: minorSchema }))
        .max(20)
        .default([]),
})
    .refine((v) => new Set(v.components.map((c) => c.componentId)).size === v.components.length, {
    message: 'A component can appear only once',
    path: ['components'],
})
    .refine((v) => v.basicMinor + v.components.reduce((s, c) => s + c.amountMinor, 0) <= MAX_MINOR, {
    message: 'Salary is too large',
    path: ['basicMinor'],
});
// ----------------------------------------------------------------- payroll
export const PAYROLL_STATUSES = ['draft', 'approved', 'paid', 'cancelled'];
export const createPayrollRunSchema = z.object({
    period: z.string().regex(/^[0-9]{4}-(0[1-9]|1[0-2])$/, 'Use YYYY-MM'),
});
export const payPayrollRunSchema = z.object({
    method: z.enum(['cash', 'bank_transfer', 'cheque']),
});
export const cancelPayrollRunSchema = z.object({ reason: text(300).min(1) });
export const listPayrollQuerySchema = z.object({ status: z.enum(PAYROLL_STATUSES).optional() });
//# sourceMappingURL=hr.js.map